/**
 * Orca Account Web - Dashboard Module
 * Handles dashboard functionality and user data display
 */

import { createClient } from '@supabase/supabase-js';

// Configuration
const CONFIG = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'https://api.orcaslicer.com',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
};

// Initialize Supabase client
let supabase;
try {
  supabase = createClient(CONFIG.supabaseUrl, CONFIG.supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true
    }
  });
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
}

// DOM Elements
const welcomeMessage = document.getElementById('welcome-message');
const welcomeDescription = document.getElementById('welcome-description');
const profileInfo = document.getElementById('profile-info');
const subscriptionInfo = document.getElementById('subscription-info');
const activityInfo = document.getElementById('activity-info');
const signoutBtn = document.getElementById('signout-btn');
const syncSettingsBtn = document.getElementById('sync-settings');
const exportDataBtn = document.getElementById('export-data');
const messageBox = document.getElementById('message');

// State
let currentUser = null;
let userProfile = null;

/**
 * Initialize dashboard
 */
async function initDashboard() {
  if (!supabase) {
    showError('Failed to initialize dashboard. Please refresh the page.');
    return;
  }

  // Check authentication
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error || !session) {
    // Redirect to sign in page
    window.location.href = '/';
    return;
  }

  currentUser = session.user;

  // Set up event listeners
  setupEventListeners();

  // Load dashboard data
  await loadDashboardData();

  // Listen for auth changes
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      window.location.href = '/';
    }
  });
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  if (signoutBtn) {
    signoutBtn.addEventListener('click', handleSignOut);
  }

  if (syncSettingsBtn) {
    syncSettingsBtn.addEventListener('click', handleSyncSettings);
  }

  if (exportDataBtn) {
    exportDataBtn.addEventListener('click', handleExportData);
  }
}

/**
 * Load dashboard data
 */
async function loadDashboardData() {
  try {
    // Load user profile
    await loadUserProfile();

    // Load subscription info
    await loadSubscriptionInfo();

    // Load recent activity
    await loadRecentActivity();

    // Update welcome message
    updateWelcomeMessage();

  } catch (error) {
    console.error('Failed to load dashboard data:', error);
    showError('Failed to load dashboard data. Please refresh the page.');
  }
}

/**
 * Load user profile
 */
async function loadUserProfile() {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', currentUser.id)
      .single();

    if (error && error.code !== 'PGRST116') { // Ignore "not found" error
      throw error;
    }

    userProfile = data;
    renderProfileInfo();

  } catch (error) {
    console.error('Failed to load user profile:', error);
    renderProfileInfo({ error: error.message });
  }
}

/**
 * Render profile information
 */
function renderProfileInfo(data = null) {
  if (!profileInfo) return;

  if (data?.error) {
    profileInfo.innerHTML = `
      <div class="text-subtle">
        <p>Unable to load profile information.</p>
        <p class="text-sm">${data.error}</p>
      </div>
    `;
    return;
  }

  const displayName = userProfile?.display_name || currentUser.email?.split('@')[0] || 'User';
  const email = currentUser.email || 'No email';
  const createdAt = userProfile?.created_at || new Date().toISOString();

  profileInfo.innerHTML = `
    <div class="user-info">
      <div class="user-avatar">
        <div class="avatar-placeholder">
          ${displayName.charAt(0).toUpperCase()}
        </div>
      </div>
      <div class="user-details">
        <h4 class="user-name">${escapeHtml(displayName)}</h4>
        <p class="user-email text-sm text-subtle">${escapeHtml(email)}</p>
        <p class="user-since text-sm text-subtle">
          Member since ${new Date(createdAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  `;
}

/**
 * Load subscription information
 */
async function loadSubscriptionInfo() {
  try {
    // Check if user has any features/entitlements
    const { data: features, error: featuresError } = await supabase
      .rpc('has_feature', { p_feature: 'premium.access' });

    if (featuresError) {
      throw featuresError;
    }

    const isPremium = features === true;

    // Get feature limits
    const { data: limits, error: limitsError } = await supabase
      .rpc('get_feature_limit', { p_feature: 'storage.projects' });

    let projectLimit = 'N/A';
    if (!limitsError && limits !== null) {
      projectLimit = limits === -1 ? 'Unlimited' : limits;
    }

    renderSubscriptionInfo({ isPremium, projectLimit });

  } catch (error) {
    console.error('Failed to load subscription info:', error);
    renderSubscriptionInfo({ error: error.message });
  }
}

/**
 * Render subscription information
 */
function renderSubscriptionInfo(data = null) {
  if (!subscriptionInfo) return;

  if (data?.error) {
    subscriptionInfo.innerHTML = `
      <div class="text-subtle">
        <p>Unable to load subscription information.</p>
        <p class="text-sm">${data.error}</p>
      </div>
    `;
    return;
  }

  const { isPremium, projectLimit } = data;

  subscriptionInfo.innerHTML = `
    <div class="subscription-info">
      <div class="plan-badge ${isPremium ? 'premium' : 'free'}">
        ${isPremium ? 'Premium' : 'Free'} Plan
      </div>
      <div class="feature-list">
        <div class="feature-item">
          <span class="feature-name">Cloud Sync</span>
          <span class="feature-status ${isPremium ? 'enabled' : 'basic'}">
            ${isPremium ? 'Enhanced' : 'Basic'}
          </span>
        </div>
        <div class="feature-item">
          <span class="feature-name">Project Storage</span>
          <span class="feature-status">${projectLimit}</span>
        </div>
        <div class="feature-item">
          <span class="feature-name">Priority Support</span>
          <span class="feature-status ${isPremium ? 'enabled' : 'disabled'}">
            ${isPremium ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
    </div>
  `;
}

/**
 * Load recent activity
 */
async function loadRecentActivity() {
  try {
    // For now, show mock activity data
    // In a real implementation, this would query an activity log table
    const activities = [
      {
        type: 'login',
        description: 'Signed in to your account',
        time: new Date().toISOString()
      },
      {
        type: 'profile_update',
        description: 'Profile information updated',
        time: new Date(Date.now() - 86400000).toISOString() // 1 day ago
      }
    ];

    renderRecentActivity(activities);

  } catch (error) {
    console.error('Failed to load recent activity:', error);
    renderRecentActivity([]);
  }
}

/**
 * Render recent activity
 */
function renderRecentActivity(activities = []) {
  if (!activityInfo) return;

  if (activities.length === 0) {
    activityInfo.innerHTML = `
      <div class="text-subtle">
        <p>No recent activity to show.</p>
      </div>
    `;
    return;
  }

  const activityHtml = activities.map(activity => `
    <div class="activity-item">
      <div class="activity-description">${escapeHtml(activity.description)}</div>
      <div class="activity-time text-sm text-subtle">
        ${formatRelativeTime(activity.time)}
      </div>
    </div>
  `).join('');

  activityInfo.innerHTML = `
    <div class="activity-list">
      ${activityHtml}
    </div>
  `;
}

/**
 * Update welcome message
 */
function updateWelcomeMessage() {
  if (!welcomeMessage || !welcomeDescription) return;

  const displayName = userProfile?.display_name || currentUser.email?.split('@')[0] || 'User';
  const hour = new Date().getHours();
  let greeting = 'Good evening';

  if (hour < 12) {
    greeting = 'Good morning';
  } else if (hour < 18) {
    greeting = 'Good afternoon';
  }

  welcomeMessage.textContent = `${greeting}, ${displayName}!`;
  welcomeDescription.textContent = 'Here\'s your account overview and quick access to your Orca Cloud features.';
}

/**
 * Handle sign out
 */
async function handleSignOut() {
  try {
    if (!supabase) return;

    await supabase.auth.signOut();
    showSuccess('Signed out successfully. Redirecting...');

    // Redirect will be handled by auth state change listener

  } catch (error) {
    console.error('Sign out error:', error);
    showError('Failed to sign out. Please try again.');
  }
}

/**
 * Handle sync settings
 */
async function handleSyncSettings() {
  if (!syncSettingsBtn) return;

  const originalText = syncSettingsBtn.innerHTML;
  syncSettingsBtn.disabled = true;
  syncSettingsBtn.innerHTML = '<span class="spinner"></span> Syncing...';

  try {
    // Simulate sync process
    await new Promise(resolve => setTimeout(resolve, 2000));

    showSuccess('Settings synchronized successfully!');

  } catch (error) {
    console.error('Sync error:', error);
    showError('Failed to sync settings. Please try again.');
  } finally {
    syncSettingsBtn.disabled = false;
    syncSettingsBtn.innerHTML = originalText;
  }
}

/**
 * Handle export data
 */
async function handleExportData() {
  if (!exportDataBtn) return;

  const originalText = exportDataBtn.innerHTML;
  exportDataBtn.disabled = true;
  exportDataBtn.innerHTML = '<span class="spinner"></span> Exporting...';

  try {
    // Prepare export data
    const exportData = {
      profile: userProfile,
      user: {
        id: currentUser.id,
        email: currentUser.email,
        created_at: currentUser.created_at
      },
      export_date: new Date().toISOString()
    };

    // Create download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orca-cloud-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showSuccess('Data exported successfully!');

  } catch (error) {
    console.error('Export error:', error);
    showError('Failed to export data. Please try again.');
  } finally {
    exportDataBtn.disabled = false;
    exportDataBtn.innerHTML = originalText;
  }
}

/**
 * Show success message
 */
function showSuccess(text) {
  showMessage(text, 'success');
}

/**
 * Show error message
 */
function showError(text) {
  showMessage(text, 'error');
}

/**
 * Show message
 */
function showMessage(text, variant = 'info') {
  if (!messageBox) return;

  messageBox.textContent = text;
  messageBox.dataset.variant = variant;
  messageBox.classList.add('is-visible');

  // Auto-hide success messages after 5 seconds
  if (variant === 'success') {
    setTimeout(() => {
      hideMessage();
    }, 5000);
  }
}

/**
 * Hide message
 */
function hideMessage() {
  if (!messageBox) return;

  messageBox.classList.remove('is-visible');
  messageBox.textContent = '';
  messageBox.removeAttribute('data-variant');
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 30) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initDashboard);